import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores/FlashesStore';
import { initStore } from '@/stores/store-utils';
import {
  Webhook,
  WebhookConfigurationPayload,
  WebhookPatchPayload,
  WebhookPostPayload,
  WebhookSecretResponse,
} from '@/types/webhooks';

export const useWebhooksStore = defineStore('Webhooks', () => {
  const webhooks = ref<Webhook[]>([]);
  const { hasPermission } = useAuth();
  const flashesStore = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      if (!hasPermission('manage:integrations')) return;

      const { data, error } = await crossbeamApi.GET('/v0.1/webhooks');
      if (error) {
        captureException(error);
        flashesStore.addErrorFlash({
          message: 'Failed to load webhooks',
        });
        return;
      }
      webhooks.value = data || [];
    },
  );

  refresh({ useCache: true });

  function getWebhookById(id: string) {
    return webhooks.value.find((webhook) => webhook.id === id);
  }

  async function createWebhook(webhookData: WebhookPostPayload) {
    const { data, error } = await crossbeamApi.POST('/v0.1/webhooks', {
      body: webhookData,
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: 'Failed to create webhook',
      });
      return;
    }

    // Add the new webhook to the list
    if (data) {
      webhooks.value.push(data);
    }

    return data;
  }

  async function editWebhookConfig(
    webhookId: string,
    config: WebhookConfigurationPayload,
  ) {
    const { data, error } = await crossbeamApi.POST(
      '/v0.1/webhooks/{webhook_id}/configuration',
      {
        body: config,
        params: {
          path: {
            webhook_id: webhookId,
          },
        },
      },
    );

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: 'Failed to update webhook configuration',
      });
      return;
    }

    // Update the webhook in the list
    if (data) {
      const index = webhooks.value.findIndex((w) => w.id === webhookId);
      if (index !== -1) {
        webhooks.value[index] = data;
      }
    }

    return data;
  }

  async function updateWebhook(
    webhookId: string,
    updates: WebhookPatchPayload,
  ) {
    const { data, error } = await crossbeamApi.PATCH(
      '/v0.1/webhooks/{webhook_id}',
      {
        body: updates,
        params: {
          path: {
            webhook_id: webhookId,
          },
        },
      },
    );

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: 'Failed to update webhook',
      });
      return;
    }

    // Update the webhook in the list
    if (data) {
      const index = webhooks.value.findIndex((w) => w.id === webhookId);
      if (index !== -1) {
        webhooks.value[index] = data;
      }
    }

    return data;
  }

  async function deleteWebhook(webhookId: string): Promise<boolean> {
    const { error } = await crossbeamApi.DELETE('/v0.1/webhooks/{webhook_id}', {
      params: {
        path: {
          webhook_id: webhookId,
        },
      },
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: 'Failed to delete webhook',
      });
      return false;
    }

    // Remove the webhook from the list
    webhooks.value = webhooks.value.filter((w) => w.id !== webhookId);
    return true;
  }

  async function createWebhookSecret(
    webhookId: string,
  ): Promise<WebhookSecretResponse | undefined> {
    const { data, error } = await crossbeamApi.POST(
      '/v0.1/webhooks/{webhook_id}/secrets',
      {
        params: {
          path: {
            webhook_id: webhookId,
          },
        },
      },
    );

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({
        message: 'Failed to create webhook secret',
      });
      return;
    }

    // Optimistically update webhook status to 'untested' in the store
    // so the user needs to test it again before it can be enabled
    const currentWebhook = getWebhookById(webhookId);
    if (currentWebhook && currentWebhook.status !== 'untested') {
      const index = webhooks.value.findIndex((w) => w.id === webhookId);
      webhooks.value[index] = {
        ...currentWebhook,
        status: 'untested' as const,
      };
    }

    // Return data but don't store it anywhere for security
    return data || undefined;
  }

  async function testWebhook(webhookId: string): Promise<void> {
    const { data, error } = await crossbeamApi.POST(
      '/v0.1/webhooks/{webhook_id}/test',
      {
        params: {
          path: {
            webhook_id: webhookId,
          },
        },
      },
    );

    if (
      error ||
      !data?.target_status ||
      data.target_status < 200 ||
      data.target_status > 299
    ) {
      flashesStore.addErrorFlash({
        message: 'Webhook test failed',
        description: 'The webhook endpoint is unresponsive, try again.',
      });
      return;
    }

    await updateWebhook(webhookId, { status: 'paused' });

    // Show success flash
    flashesStore.addSuccessFlash({
      message: 'Webhook test successful',
    });
  }

  const activeWebhooks = computed(() =>
    webhooks.value.filter((webhook) => webhook.status === 'active'),
  );

  const pausedWebhooks = computed(() =>
    webhooks.value.filter((webhook) => webhook.status === 'paused'),
  );

  const untestedWebhooks = computed(() =>
    webhooks.value.filter((webhook) => webhook.status === 'untested'),
  );

  return {
    webhooks,
    activeWebhooks,
    pausedWebhooks,
    untestedWebhooks,
    initError,
    ready,
    readySync,
    running,
    refresh,
    getWebhookById,
    createWebhook,
    editWebhookConfig,
    updateWebhook,
    deleteWebhook,
    createWebhookSecret,
    testWebhook,
  };
});
